import { inject, Injectable, Injector } from "@angular/core";
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  where
} from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { defaultLanguage } from "@interfaces/translation-interface";
import { BehaviorSubject, EMPTY, firstValueFrom, from, Observable, of } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take } from "rxjs/operators";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";
import { SafeLocalStorageService } from "./safe-local-storage.service";

@Injectable({ providedIn: "root" })
export class SyndicateDataService {
  private safeLocalStorage = inject(SafeLocalStorageService);
  selectedlanguage$ = new BehaviorSubject(this.safeLocalStorage.getItem("language_code") || "");
  private syndicateDataSubject = new BehaviorSubject({});
  private syndicateReset = false;
  private isNewSydicateRouting = false;
  private syndicateID = 0;
  private syndicateShareLink: string = "";
  selectedLanguage: string;
  private authService: AuthService;
  private authStateService = inject(AuthStateService);

  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private firestoreDataService: FirestoreDataService,
    private injector: Injector,
    private commonService: CommonService,
  ) {
    // Get AuthService from injector to avoid circular dependency
    this.authService = this.injector.get(AuthService);
    this.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l ? l : defaultLanguage;
        if (l) {
          // translate.use(l ? l : defaultLanguage);
        }
      },
      (err) => {
        return err;
      }
    );
  }

  public async getTooltipVipStatus() {
    try {
      const userRef = doc(this.firestore, "/Global_lookup/syndicate_page_offer"); // Reference Firestore document
      const userSnap = await getDoc(userRef); // Fetch document snapshot

      const userData = userSnap?.data(); // Get the document data
      const productReference = userData?.["product_reference"]; // Get product reference

      if (productReference) {
        try {
          const productSnapshot = await getDoc(productReference); // Fetch referenced product snapshot
          if (productSnapshot?.exists()) {
            userData["product_reference"] = productSnapshot.data(); // Update userData with product details
          } else {
            return { success: false, code: "DOCUMENT_NOT_FOUND", message: "Document does not exist" };
          }
        } catch (refError) {
          // Handle permission errors on product reference gracefully
          console.warn("[getTooltipVipStatus] Error fetching product reference:", refError?.message || refError);
          // Continue with userData without product reference instead of throwing
          userData["product_reference"] = null;
        }
      }

      return { success: true, data: userData };
    } catch (err) {
      // Check if this is a permission error - log with lower severity
      if (err?.code === "permission-denied") {
        console.warn("[getTooltipVipStatus] Permission denied accessing page-offer data:", err.message);
      } else {
        console.error("[getTooltipVipStatus] Error fetching page-offer data:", err); // Log error
      }
      // Return valid empty response instead of error object to prevent RxJS stream issues
      return { success: false, code: "FETCH_ERROR", message: "Error fetching page-offer data", data: null };
    }
  }

  setSyndicateData(syndicateData) {
    this.setSyndicateID(syndicateData?.sId);
    this.syndicateDataSubject.next(syndicateData);
  }

  getSyndicateData() {
    return this.syndicateDataSubject.asObservable();
  }

  stopResetSyndicateForm() {
    this.syndicateReset = false;
  }

  resetSyndicateForm() {
    this.syndicateReset = true;
  }

  getResetStatus() {
    return this.syndicateReset;
  }

  setNewSyndicateRouting(state: boolean) {
    this.isNewSydicateRouting = state;
  }

  getSyndicateRouting() {
    return this.isNewSydicateRouting;
  }

  setSyndicateID(syndicateID: number) {
    this.syndicateID = syndicateID;
  }

  setSyndicateShareLink(link: string) {
    this.syndicateShareLink = link;
  }

  getSyndicateShareLink() {
    return this.syndicateShareLink as string;
  }

  getSyndicateID(): string {
    return this.syndicateID.toString(); // Ensure it always returns a string
  }


  async getSyndicateInviteLink(syndicateID: string | number): Promise<string> {
    try {
      const userId = await firstValueFrom(
        this.commonService.getUid("SyndicateService")
      );
      if (!userId || userId === "null") {
        throw new Error("Invalid userId");
      }

      const psInviteLinkRef = doc(this.firestore, "urls/psInviteLink");

      // Always start fresh
      await deleteDoc(psInviteLinkRef);

      if (this.getSyndicateShareLink()) {
        return this.getSyndicateShareLink();
      }

      if (syndicateID === 0) {
        throw new Error("Invalid syndicateID");
      }

      // Safe access to document with fallback for SSR and mobile browsers
      const origin = (typeof document !== 'undefined' && document?.location?.origin) 
        ? document.location.origin 
        : 'https://app.lottosocial.com';
      
      const inviteUrl = `${origin}?psId=${syndicateID}&memberId=${userId}`;
      const path_id = "SWswmrDeZWnAkAh2BShV";
      const doc_data = { url: inviteUrl };
      const method = "update";

      await firstValueFrom(
        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).pipe(take(1))
      );

      const docSnap = await getDoc(psInviteLinkRef);
      const data = docSnap.data();

      if (
        data &&
        data.shortUrl &&
        data.shortUrl !== "" &&
        data.url?.split("?")[1].split("&")[0].split("=")[1] == syndicateID &&
        data.url?.split("?")[1].split("&")[1].split("=")[1] == userId
      ) {
        const path_id = `h8gviqcFcM9gmL6FyegX`;
        const doc_data = { inviteURL: data.shortUrl };
        const dv = { docId: String(syndicateID) };
        const method = "update";
        await this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).subscribe({
          error: (err) => {
            return err;
          },
        });
        return data.shortUrl as string;
      }
      throw new Error("Invalid invite link data");
    } catch (err) {
      throw err;
    }
  }


  /**
   * Listen to a Firestore doc via onSnapshot. On listener errors (e.g. iOS
   * background network drops), emit null instead of erroring the Observable,
   * then recreate the listener so the stream can recover. Never call
   * observer.error — that caused unhandled rejections on the syndicate page.
   */
  private listenDocEmittingNullOnError(
    docRef: ReturnType<typeof doc>,
    logLabel: string
  ): Observable<DocumentData | undefined | null> {
    return new Observable((observer) => {
      let unsubscribe: (() => void) | undefined;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      let stopped = false;

      const attach = () => {
        unsubscribe?.();
        unsubscribe = onSnapshot(
          docRef,
          (docSnap) => {
            if (!stopped) {
              observer.next(docSnap?.data());
            }
          },
          (err) => {
            this.authService.errorHandlerLogs(`[${logLabel}] Firestore error:`, err);
            if (stopped) {
              return;
            }
            observer.next(null);
            // Recreate after a short delay so transient drops can recover
            // instead of permanently completing (and shareReplay-caching null).
            retryTimer = setTimeout(() => {
              if (!stopped) {
                attach();
              }
            }, 2000);
          }
        );
      };

      attach();

      return () => {
        stopped = true;
        if (retryTimer) {
          clearTimeout(retryTimer);
        }
        unsubscribe?.();
      };
    }).pipe(
      catchError((err) => {
        this.authService.errorHandlerLogs(`[${logLabel}] Unexpected error:`, err);
        return of(null);
      })
    );
  }

  getSyndicateSummaryMarkup() {
    const docRef = doc(this.firestore, "Global_lookup/ps_summary"); // Reference Firestore document
    return this.listenDocEmittingNullOnError(docRef, "getSyndicateSummaryMarkup");
  }

  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): Data[] {
    // Add type guard to ensure actions is an array before mapping
    if (!Array.isArray(actions)) {
      this.authService.errorHandlerLogs('snapShotData: Input is not an array, returning empty array', { inputType: typeof actions });
      return [];
    }
    
    if (!actions) {
      return []; // or handle the null case appropriately
    }
    return actions.map((a: Data) => {
      const data = a?.payload?.doc?.data() as Data;
      const id = a?.payload?.doc?.id;
      return { id, ...data };
    });
  }

  GetWinMorePromise() {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (isOnline) {
          const docRef = doc(this.firestore, `/general_modules/prosub_id/${localStorage.getItem("prosub_id")}/details`); // Reference Firestore document
          return from(getDoc(docRef)).pipe(
            map((docSnap) => docSnap?.data()), // Extract document data
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            catchError((error) => {
              return of(null);
            })
          );
        } else {
          return EMPTY;
        }
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      catchError((error) => {
        return of(null); 
      }),
      shareReplay(1)
    );
  }

  // Get win more details

  getWinMoreDetails() {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return EMPTY;
        }

        return this.commonService.getUid("syndicateDataService").pipe(
          take(1),
          switchMap((userId) => {
            if (!userId || userId === "null") {
              return EMPTY;
            }

            const collectionRef = collection(this.firestore, `users/${userId}/userSyndicate_list`);

            const q = query(
              collectionRef,
              where("win_more_promise_details.end_date", ">", new Date()),
              orderBy("win_more_promise_details.end_date", "asc")
            );

            return from(getDocs(q)).pipe(
              map((querySnapshot) =>
                querySnapshot.docs.map((doc) => ({
                  type: "added", // mimic DocumentChangeAction
                  payload: { doc }, // mimic AngularFirestore snapshot structure
                }))
              ),
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          })
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((res: any[]) => this.snapShotData(res)),
      shareReplay(1)
    );
  }


  async getSyndicateName(userId: string): Promise<any[]> {
    const collectionRef = collection(this.firestore, `users/${userId}/userSyndicate_list`);
    const queryRef = query(collectionRef, orderBy("creation_date", "desc")); // Replace 'yourFieldName' with the actual field
    const snapshot = await getDocs(queryRef);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  }

  getWinMoreInboxMessage() {
    const docRef = doc(this.firestore, `/Global_lookup/win-more-start`); // Reference Firestore document
    return this.listenDocEmittingNullOnError(docRef, "getWinMoreInboxMessage").pipe(
      // refCount resets the shared stream when all subscribers leave so a
      // transient null is not cached for the lifetime of the service.
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
  getWinMoreInboxEnd() {
    const docRef = doc(this.firestore, `Global_lookup/win-more-end`); // Reference Firestore document
    return this.listenDocEmittingNullOnError(docRef, "getWinMoreInboxEnd").pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
  getWinMoreInboxStart() {
    const docUrl = `/Global_lookup/user_messages_templates/inbox_message/inbox_win_more_promise/i18n/inbox_win_more_promise_${this.selectedLanguage}`;
    const docRef = doc(this.firestore, docUrl); // Reference Firestore document
    return getDoc(docRef).then((docSnap) => {
      const data = docSnap?.data(); // Extract document data
      return data;
    });
  }

  getSyndicatePageData() {
    // Gate behind auth readiness: wait for any in-progress custom-token refresh
    // to complete before opening the onSnapshot listener.  Starting a Firestore
    // read while the token is being renewed causes a permission-denied race
    // condition (LOTTOSOCIAL-PWA-1MED).  tokenRefreshInProgress$ is already
    // false for established sessions, so there is no perceptible latency added
    // in the common case.
    return this.authService.tokenRefreshInProgress$.pipe(
      filter(inProgress => !inProgress),
      take(1),
      switchMap(() => this.commonService.getUid("syndicateDataService").pipe(take(1))),
      switchMap((userId) => {
        if (!userId || userId === "null") {
          return of({ syndicate_list: { syndicate_cards: [] } }); // No user, return empty structure
        }

        // Gate on customTokenReady$ before opening the syndicate page listener.
        // This prevents permission-denied errors on iOS Safari when the listener
        // fires before the Firebase custom token has been received.
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => {
            const docRef = doc(this.firestore, `users/${userId}/main_pages/syndicate`);

            return new Observable((observer) => {
              const unsubscribe = onSnapshot(
                docRef,
                (docSnap) => {
                  const data = docSnap?.data();
                  if (data?.syndicate_list?.syndicate_cards) {
                    data.syndicate_list.syndicate_cards.forEach((card: Data) => {
                      if (card?.syndicate_end_date && typeof card.syndicate_end_date !== "string") {
                        try {
                          // Safely convert Firestore Timestamp to string
                          const dateObj = card.syndicate_end_date.toDate();
                          // Ensure we always get a valid string or empty string, never undefined
                          card.syndicate_end_date = dateObj && !isNaN(dateObj.getTime()) ? dateObj.toString() : "";
                        } catch (error) {
                          console.error("Error converting syndicate_end_date:", error);
                          card.syndicate_end_date = ""; // Fallback to empty string
                        }
                      } else if (!card?.syndicate_end_date) {
                        // If syndicate_end_date is null, undefined, or missing, set to empty string
                        card.syndicate_end_date = "";
                      }
                    });
                  }
                  observer.next(data);
                },
                (error) => {
                  console.error("Error fetching syndicate data:", error);
                  observer.error(error);
                }
              );

              // Clean up subscription
              return () => unsubscribe();
            }).pipe(
              map((res: any) => {
                if (res?.syndicate_list?.syndicate_cards) {
                  res.syndicate_list.syndicate_cards.forEach((syndicate: Data) => {
                    // Double-check to ensure syndicate_end_date is always a string
                    if (typeof syndicate?.syndicate_end_date !== "string" || syndicate?.syndicate_end_date === null || syndicate?.syndicate_end_date === undefined) {
                      syndicate.syndicate_end_date = "";
                    }
                  });
                }
                return res;
              }),
              catchError((error) => {
                console.error("Error fetching syndicate data:", error);
                return of([]);
              }),
              shareReplay(1)
            );
          })
        );
      }),
      catchError(() => of([])) // Guard in case getUid fails
    );
  }


  // Gets the Syndicate Jackpot Triggers for the user
  // Parameters:
  // userId: The user ID for the user who is getting the Jackpot Triggers
  // Returns: The Jackpot Triggers for the user
  getSyndicateJackpotTriggers(userId: string) {
    // Validate userId before making Firestore call
    if (!userId || userId === "null" || userId.length < 5) {
      console.warn('[getSyndicateJackpotTriggers] Invalid userId:', userId);
      return of([]);
    }

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Return null if offline
        }

        const jackpotTriggerCollection = collection(this.firestore, `/users/${userId}/jackpot_trigger`); // Reference collection
        return from(getDocs(jackpotTriggerCollection)).pipe(
          this.catchService.retryWhen,
          catchError((error) => {
            // Check if it's a permission error
            if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
              console.warn('[getSyndicateJackpotTriggers] Permission denied for userId:', userId, error);
              return of(null); // Return null to prevent stream breakage
            }
            console.error('[getSyndicateJackpotTriggers] Error fetching syndicate jackpot triggers:', error);
            return of(null); // Return null on error to prevent stream breakage
          })
        );
      }),
      map((snapshot: QuerySnapshot<DocumentData>) => {
        if (!snapshot) {
          return []; // Handle null snapshot
        }
        return this.firestoreDataService.snapShotData(snapshot); // Pass QuerySnapshot directly to snapShotData
      }),
      catchError((error) => {
        console.error('[getSyndicateJackpotTriggers] Outer catchError:', error);
        return of([]); // Always return empty array on any error
      }),
      this.catchService.catchOfflineError,
      shareReplay(1) // Cache results
    );
  }
  // This code gets the user's request to join a syndicate and stores it in a
  // BehaviorSubject. The BehaviorSubject is then subscribed to by the
  // syndicate-join component, which displays the user's request to join the
  // syndicate.
  public userSyndicateRequest$ = new BehaviorSubject(5);

  // This method has beed used to update Page
  updateSyndicateRequest(pageSize: number) {
    if (typeof pageSize !== "number" || pageSize <= 0) {
      throw new Error("Invalid page size");
    }
    this.userSyndicateRequest$.next(pageSize);
  }


  // This function returns the user's syndicate list from Firebase, based on the page size passed to it.
  // The user's syndicate list is stored in Firebase in the userSyndicate_list collection, and this is where the function
  //  retrieves the list from. The list is ordered by creation date, and the number of items retrieved is based on the page size.
  // The function returns null if the user is not logged in. [ we already move]
  userSyndicate$ = this.userSyndicateRequest$.pipe(
    switchMap((pageSize: number) =>
      this.commonService.getUid("SyndicateDataService").pipe(
        take(1),
        switchMap((userId) => {
          if (!userId || userId.length <= 5) {
            return of(null); // invalid uid
          }

          return this.catchService.isOnline$.pipe(
            switchMap(async (isOnline) => {
              if (!isOnline) {
                return []; // offline → return empty
              }

              const syndicateCollection = collection(
                this.firestore,
                `/users/${userId}/userSyndicate_list`
              );

              const syndicateQuery = query(
                syndicateCollection,
                where("hide", "==", false),
                orderBy("creation_date", "desc"),
                limit(pageSize)
              );

              const snapshot = await getDocs(syndicateQuery);
              return snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
            }),
            map((actions: any[]) => this.snapShotData(actions)),
            shareReplay(1)
          );
        })
      )
    ),
    shareReplay(1)
  );

}
